import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, History, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { errorMessage, useMobileApiLog, useMobileApiRow } from "../../api";
import { EmptyBlock, ErrorBlock, FetchingDot, KeyValueGrid, LoadingBlock, SectionCard } from "../../components/primitives";
import { formatMs, formatStamp } from "../../format";
import type { MobileApiBody, MobileApiRow, MobileApiStatus, MonitoringWindow } from "../../types";
import { CopyButton } from "../events/bits";

const WINDOW_MS: Record<MonitoringWindow, number> = {
  "1h": 3_600_000,
  "6h": 6 * 3_600_000,
  "24h": 86_400_000,
  "7d": 7 * 86_400_000,
};
const PAGE = 100;
const MAX_LIMIT = 1000;
const ALL = "__all";
const STATUSES: MobileApiStatus[] = ["2xx", "4xx", "5xx"];

const statusTone = (status: number) =>
  status >= 500
    ? "border-red-500/40 bg-red-500/15 text-red-300"
    : status >= 400
      ? "border-amber-500/35 text-amber-300"
      : "border-emerald-500/30 text-emerald-300";

/**
 * What the BiteBerry tablet app (and our station tablets) sent to the mobile API and what we answered,
 * newest first; a row opens the headers and both bodies.
 */
export function MobileApiLogCard({ window, onOpen }: { window: MonitoringWindow; onOpen: (itemId: string) => void }) {
  const { t } = useLocalization();
  const [status, setStatus] = useState<MobileApiStatus | null>(null);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState<string | null>(null);

  // the search runs when typing pauses, not on every key
  useEffect(() => {
    const timer = setTimeout(() => setQ(text), 300);
    return () => clearTimeout(timer);
  }, [text]);
  useEffect(() => setLimit(PAGE), [status, q, window]);

  // a stable start of the window (minute precision), so polling does not change the query key
  const minute = Math.floor(Date.now() / 60_000);
  const sinceMs = useMemo(() => minute * 60_000 - WINDOW_MS[window], [minute, window]);
  const query = useMobileApiLog(sinceMs, { status, q, pizzaId: null, limit });
  const rows = query.data?.rows ?? [];

  return (
    <SectionCard
      title={t("monitoring.bb.mobile.title")}
      description={t("monitoring.bb.mobile.description")}
      actions={<FetchingDot active={query.isFetching && !query.isPending} />}
      contentClassName="p-0"
    >
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={status ?? ALL}
          onValueChange={(value) => value && setStatus(value === ALL ? null : (value as MobileApiStatus))}
          aria-label={t("monitoring.bb.mobile.status")}
          className="gap-0.5"
        >
          <ToggleGroupItem value={ALL} className="h-8 px-2.5 text-xs">
            {t("monitoring.bb.mobile.status.all")}
          </ToggleGroupItem>
          {STATUSES.map((s) => (
            <ToggleGroupItem key={s} value={s} className="h-8 px-2.5 font-mono text-xs">
              {s}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("monitoring.bb.mobile.search")}
            aria-label={t("monitoring.bb.mobile.search")}
            className="h-8 pl-7 text-xs"
          />
        </div>
      </div>
      {query.isError && (
        <div className="px-4 pt-3">
          <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        </div>
      )}
      {!query.data ? (
        !query.isError && (
          <div className="p-4">
            <LoadingBlock height={160} />
          </div>
        )
      ) : rows.length === 0 ? (
        <div className="p-4">
          <EmptyBlock>{t("monitoring.bb.mobile.empty")}</EmptyBlock>
        </div>
      ) : (
        <>
          <ul className="max-h-[40rem] divide-y divide-border/60 overflow-y-auto">
            {rows.map((row) => (
              <RequestRow
                key={row.id}
                row={row}
                open={open === row.id}
                onToggle={() => setOpen(open === row.id ? null : row.id)}
                onOpenPizza={onOpen}
              />
            ))}
          </ul>
          {query.data.limited && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-[11px] text-muted-foreground">
              <span>{t("monitoring.bb.log.limited", { limit: query.data.limit })}</span>
              {limit < MAX_LIMIT && (
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setLimit(Math.min(MAX_LIMIT, limit + 2 * PAGE))}>
                  {t("monitoring.bb.log.loadMore")}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

function RequestRow({
  row,
  open,
  onToggle,
  onOpenPizza,
}: {
  row: MobileApiRow;
  open: boolean;
  onToggle: () => void;
  onOpenPizza: (itemId: string) => void;
}) {
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className={cn(open && "bg-muted/20")}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full min-w-0 items-start gap-2 px-4 py-2 text-left hover:bg-muted/30 focus-visible:bg-muted/30 focus-visible:outline-none"
      >
        <Chevron className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="w-[7.5rem] shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{formatStamp(row.ts)}</span>
            <span className={cn("inline-flex h-5 shrink-0 items-center rounded-md border px-1.5 font-mono text-[10px] font-semibold", statusTone(row.status))}>
              {row.status}
            </span>
            <span className="shrink-0 font-mono text-[11px] font-semibold">{row.method}</span>
            <span className="min-w-0 flex-1 break-all font-mono text-[11px]">{row.path}</span>
            <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{formatMs(row.ms)}</span>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground sm:pl-[7.5rem]">
            {row.summary && (
              <span className={cn("break-words", row.status >= 400 ? "text-red-300/90" : "text-foreground/80")}>{row.summary}</span>
            )}
            {row.login && <span className="break-all">{row.login}</span>}
            {row.pizza_id && <span className="break-all font-mono">{row.pizza_id}</span>}
          </div>
        </div>
      </button>
      {open && <RequestDetail row={row} onOpenPizza={onOpenPizza} />}
    </li>
  );
}

function RequestDetail({ row, onOpenPizza }: { row: MobileApiRow; onOpenPizza: (itemId: string) => void }) {
  const { t } = useLocalization();
  const detail = useMobileApiRow(row.id);
  const full = detail.data;
  const items: { label: ReactNode; value: ReactNode }[] = [
    { label: t("monitoring.bb.mobile.field.received"), value: formatStamp(row.ts) },
    { label: t("monitoring.bb.mobile.field.duration"), value: formatMs(row.ms) },
  ];
  if (row.login || row.user_id) items.push({ label: t("monitoring.bb.mobile.field.user"), value: row.login ?? row.user_id });
  if (row.branch_id) items.push({ label: t("monitoring.bb.field.branch"), value: row.branch_id });
  if (full?.scope) items.push({ label: t("monitoring.bb.mobile.field.scope"), value: full.scope });
  if (full?.query) items.push({ label: t("monitoring.bb.mobile.field.query"), value: <span className="break-all font-mono">{full.query}</span> });
  if (row.ip) items.push({ label: t("monitoring.bb.mobile.field.ip"), value: row.ip });
  if (row.user_agent) items.push({ label: t("monitoring.bb.mobile.field.userAgent"), value: <span className="break-all">{row.user_agent}</span> });
  if (full?.instance) items.push({ label: t("monitoring.bb.field.instance"), value: full.instance });

  return (
    <div className="flex min-w-0 flex-col gap-3 px-4 pb-3 pl-9">
      <KeyValueGrid items={items} />
      {row.pizza_id && (
        <div>
          <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={() => onOpenPizza(row.pizza_id!)}>
            <History className="h-3.5 w-3.5" aria-hidden />
            {t("monitoring.bb.log.timeline")}
          </Button>
        </div>
      )}
      {detail.isPending ? (
        <LoadingBlock height={120} />
      ) : detail.isError ? (
        <ErrorBlock message={errorMessage(detail.error)} onRetry={() => void detail.refetch()} />
      ) : full ? (
        <div className="grid min-w-0 grid-cols-1 gap-3 xl:grid-cols-2">
          <BodyBlock title={t("monitoring.bb.mobile.request")} body={full.request} headers={full.request_headers} />
          <BodyBlock title={t("monitoring.bb.mobile.response")} body={full.response} />
        </div>
      ) : null}
    </div>
  );
}

function BodyBlock({ title, body, headers }: { title: string; body: MobileApiBody; headers?: Record<string, string> }) {
  const { t } = useLocalization();
  const text =
    body?.json !== undefined ? JSON.stringify(body.json, null, 2) : body?.text !== undefined ? body.text : null;
  const headerText = headers && Object.keys(headers).length ? JSON.stringify(headers, null, 2) : null;
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-muted-foreground">
          {title}
          {body?.content_type ? <span className="ml-1.5 font-mono font-normal">{body.content_type}</span> : null}
          {body?.bytes !== null && body?.bytes !== undefined ? (
            <span className="ml-1.5 font-normal">· {t("monitoring.bb.mobile.bytes", { bytes: body.bytes })}</span>
          ) : null}
        </span>
        {text && <CopyButton text={text} what={title} />}
      </div>
      {text ? (
        <pre
          tabIndex={0}
          aria-label={title}
          className="max-h-96 min-w-0 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/30 p-2 font-mono text-[11px] leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {text}
        </pre>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          {body?.bytes ? t("monitoring.bb.mobile.binary") : t("monitoring.bb.mobile.noBody")}
        </p>
      )}
      {body?.truncated && <p className="text-[11px] text-amber-300">{t("monitoring.bb.log.payloadTruncated")}</p>}
      {headerText && (
        <details className="min-w-0">
          <summary className="cursor-pointer text-[11px] text-muted-foreground">{t("monitoring.bb.mobile.headers")}</summary>
          <pre className="mt-1 max-h-64 min-w-0 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/30 p-2 font-mono text-[11px] leading-relaxed">
            {headerText}
          </pre>
        </details>
      )}
    </div>
  );
}
